/**
 * In-app events in the web app (docs/APP-EVENTS.md; core appevents.ts and appevents-tracker.ts). The same queue as the phone's,
 * kept in localStorage:
 *  - the screen: the route pattern the address fits (core webScreen over App.tsx's routes); never a health screen (E1);
 *  - taps: ONE click listener on the document (capture), so no screen adds anything: a control is named only by a data-testid /
 *    data-cc / label a developer wrote (the generated APP_CONTROLS, E2), never by its text when that isn't one of them;
 *  - outbound links: any link to another site, with its host; a buy link also with its item and placement (data-item /
 *    data-placement on the link or around it). An Amazon link gets its placement's tracking ID as it opens (core retagAmazon).
 *  - Ask, the step-by-step player and Setup tell it a flow's start and finish.
 * Nothing at all under Do Not Track or Global Privacy Control, when "Share usage data" is off, for a home, or signed out. Sent
 * about every 30 seconds and as the tab hides (fetch keepalive, with the sign-in; sendBeacon can't carry it). The browser's own
 * sender exists only in a real build or the in-browser demo, never under tests or the dev server (realSender below).
 */
import {
  EventTracker, FLUSH_EVERY_MS, isCatalogItem, outboundHost, retagAmazon, webScreen, BUY_PLACEMENTS,
  answerGotIt, markGotItAsked, noteBuyClick, pruneGotIt, type AppFlow, type BuyPlacement, type EventKV, type FlowStage, type GotItState,
} from "@logwave/core";
import { WEB_ROUTES as APP_ROUTES } from "@logwave/core/src/generated/appcontrols-web.ts";
import "./controlsEager.ts";
import { loadControls, loadedControls } from "./controlList.ts";
import { IS_MOCK, MOCK_BUILD } from "../env.ts";
import { optedOut } from "./siteAnalytics.ts";

export const SHARE_USAGE_KEY = "cc.shareUsage";
export const GOT_IT_KEY = "cc.gotit.v1";

/** localStorage as the queue's store (blocked storage: the queue lives in memory only). */
export function localKV(win: Window = window): EventKV {
  const ls = (): Storage | undefined => { try { return win.localStorage; } catch { return undefined; } };
  return {
    getItem: async (k) => { try { return ls()?.getItem(k) ?? null; } catch { return null; } },
    setItem: async (k, v) => { try { ls()?.setItem(k, v); } catch { /* full or blocked */ } },
  };
}

export const makeTracker = (kv: EventKV | null, now?: () => number): EventTracker => {
  const t = new EventTracker(kv, loadedControls(), now);
  if (!loadedControls()) void loadControls().then((l) => t.setControls(l), () => { /* the list did not load: taps are not counted */ });
  return t;
};
/** The app's one tracker (AppEventsBridge configures it). Tests make their own. */
export let tracker: EventTracker = makeTracker(null);
export function setTracker(t: EventTracker): void { tracker = t; }

/** The person's choice in this browser: true, false, or null (never chosen: the default applies). */
export function usageChoice(win: Window = window): boolean | null {
  try { const v = win.localStorage.getItem(SHARE_USAGE_KEY); return v === "1" ? true : v === "0" ? false : null; } catch { return null; }
}
export function setUsageChoice(on: boolean, win: Window = window): void {
  try { win.localStorage.setItem(SHARE_USAGE_KEY, on ? "1" : "0"); if (!on) win.localStorage.removeItem(GOT_IT_KEY); } catch { /* blocked */ }
}

/** The screen for an address (null when it fits no route; core webScreen). */
export const screenOf = (pathname: string): string | null => webScreen(pathname, APP_ROUTES);

/**
 * Whether this build may send at all: a real build, or the in-browser demo (its API is in memory). Never under Bun's tests or the
 * dev server against a real API (MOCK_BUILD true there): a test DOM's fetch is a real request (it hung a test suite before).
 */
export const realSender = (mockBuild = MOCK_BUILD, isMock = IS_MOCK) => !mockBuild || isMock;

/* ─────────────── the one click listener ─────────────── */

const placementOf = (v: string | null | undefined): BuyPlacement | undefined => (v && (BUY_PLACEMENTS as readonly string[]).includes(v) ? (v as BuyPlacement) : undefined);
/** Amazon tags: the default (VITE_AMAZON_TAG) and per placement (VITE_AMAZON_TAGS). */
export interface BuyTags { default?: string; byPlacement: Partial<Record<BuyPlacement, string>> }

/**
 * What one click becomes: an outbound link (host, item, placement; an Amazon link retagged) or a tap. Returns what it did, for
 * tests. `gotIt` remembers a buy link with an item for "Did you get it?".
 */
export function onDocumentClick(ev: Event, t: EventTracker, tags: BuyTags, win: Window = window, now = Date.now()): "out" | "tap" | null {
  const target = ev.target as Element | null;
  if (!target || typeof target.closest !== "function" || !t.on) return null;
  if (target.closest("[data-no-track]")) return null;
  const a = target.closest("a[href]") as HTMLAnchorElement | null;
  const host = a ? outboundHost(a.href) : null;
  if (a && host && host !== win.location.hostname.replace(/^www\./, "")) {
    const box = a.closest("[data-placement],[data-item]") as HTMLElement | null;
    const placement = placementOf(a.dataset.placement ?? box?.dataset.placement) ?? "other";
    const item = a.dataset.item ?? box?.dataset.item;
    const url = retagAmazon(a.href, placement, tags.default, tags.byPlacement);
    if (url !== a.href) a.href = url; // the default action reads the address after this listener
    t.outbound(url, { ...(isCatalogItem(item) ? { item } : {}), placement });
    // A link named with data-cc (a cited plan's "Get it": cc:supply-<step>) is also counted as a tap on that control.
    const cc = a.getAttribute("data-cc");
    if (cc) t.tap({ testID: cc, kind: "link" });
    const name = a.dataset.itemName ?? box?.dataset.itemName;
    if (item && name && isCatalogItem(item)) saveGotIt(pruneGotIt(noteBuyClick(loadGotIt(win), { item, name, placement }, now), now), win);
    return "out";
  }
  const control = target.closest("button,a,[role=button],[role=tab],[role=link],summary,[data-testid],[data-cc]") as HTMLElement | null;
  if (!control) return null;
  const named = control.closest("[data-testid],[data-cc]") as HTMLElement | null;
  const testID = named?.getAttribute("data-testid") ?? named?.getAttribute("data-cc") ?? undefined;
  const text = (control.getAttribute("aria-label") ?? control.textContent ?? "").replace(/\s+/g, " ").trim();
  const kind = control.tagName === "A" || control.getAttribute("role") === "link" ? "link" as const : "button" as const;
  return t.tap({ ...(testID ? { testID } : {}), ...(text ? { label: text } : {}), kind }) ? "tap" : null;
}

/* ─────────────── "Did you get it?" memory (this browser only) ─────────────── */

export function loadGotIt(win: Window = window): GotItState {
  try { return JSON.parse(win.localStorage.getItem(GOT_IT_KEY) ?? "{}") as GotItState; } catch { return {}; }
}
export function saveGotIt(st: GotItState, win: Window = window): void {
  try { win.localStorage.setItem(GOT_IT_KEY, JSON.stringify(st)); } catch { /* blocked: no question later */ }
}
export const gotItAsked = (item: string, now: number, win: Window = window) => saveGotIt(markGotItAsked(loadGotIt(win), item, now), win);
export const gotItAnswered = (item: string, a: "yes" | "not_yet", now: number, win: Window = window) => saveGotIt(answerGotIt(loadGotIt(win), item, a, now), win);

/* ─────────────── wiring ─────────────── */

/**
 * Starts the listener, the 30-second sends and the send as the tab hides. Returns the stop. `send` is the API's (normal and
 * keepalive); nothing starts unless the tracker is on.
 */
export function startAppEvents(t: EventTracker, tags: BuyTags, win: Window = window): () => void {
  const click = (ev: Event) => { try { onDocumentClick(ev, t, tags, win); } catch { /* never in the way */ } };
  // A hidden tab stops the screen's clock (a new session after 30 minutes away) and sends what waits.
  const hide = () => { if (win.document.visibilityState === "hidden") { t.pause(); void t.flush(true); } else t.resume(); };
  const timer = win.setInterval(() => { void t.flush(); }, FLUSH_EVERY_MS);
  win.document.addEventListener("click", click, { capture: true });
  win.document.addEventListener("visibilitychange", hide);
  const gone = () => { t.pause(); t.end(); void t.flush(true); };
  win.addEventListener("pagehide", gone);
  return () => {
    win.clearInterval(timer);
    win.document.removeEventListener("click", click, { capture: true });
    win.document.removeEventListener("visibilitychange", hide);
    win.removeEventListener("pagehide", gone);
  };
}

export const trackFlow = (f: AppFlow, st: FlowStage) => { try { tracker.flow(f, st); } catch { /* never in the way */ } };
export const trackFlowOnce = (f: AppFlow, st: FlowStage) => { try { void tracker.flowOnce(f, st); } catch { /* never in the way */ } };
export { optedOut };
